import { useEffect, useState } from 'react';
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material';
import useGetResponseMarketing from '../../../hooks/useGetResponseMarketing';

// The campaigns endpoint may return a bare array or { campaigns: [...] }.
const normalizeCampaignList = data => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.campaigns)) return data.campaigns;
  return [];
};

// The tags endpoint may return a bare array or { tags: [...] }.
const normalizeTagList = data => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.tags)) return data.tags;
  return [];
};

// Read a campaign's identifier regardless of the casing the API used.
const campaignId = campaign =>
  campaign ? String(campaign.campaignId || campaign.campaignID || campaign.id || '') : '';

const parseTags = value =>
  value
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);

// Settings for the "GetResponse-Tag" block: assigns/removes GetResponse tags on
// the contact. Tags are the trigger signals of the GetResponse automations.
const GetResponseTagSettings = ({ block, readOnly, onConfigChange }) => {
  const { getCampaigns, getTags } = useGetResponseMarketing();
  const config = block.config || {};
  const [campaigns, setCampaigns] = useState([]);
  const [knownTags, setKnownTags] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [campaignsResult, tagsResult] = await Promise.all([getCampaigns(), getTags()]);
        if (!active) return;
        if (campaignsResult !== null) setCampaigns(normalizeCampaignList(campaignsResult));
        if (tagsResult !== null) {
          setKnownTags(
            normalizeTagList(tagsResult)
              .map(tag => tag.name)
              .filter(Boolean)
          );
        }
      } catch {
        // Campaign/tag suggestions are optional; free-text tags always work.
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTags = config.addTags || [];
  const removeTags = config.removeTags || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        select
        size='small'
        label='GetResponse-Liste'
        value={config.campaignId || ''}
        disabled={readOnly}
        onChange={event => onConfigChange({ campaignId: event.target.value || null })}
      >
        <MenuItem value=''>Standard-Liste (Umgebung)</MenuItem>
        {campaigns.map(campaign => (
          <MenuItem key={campaignId(campaign)} value={campaignId(campaign)}>
            {campaign.name || campaignId(campaign)}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextField
          key={`${block.blockId}-addTags`}
          size='small'
          label='Tags setzen (kommagetrennt)'
          placeholder='birthday_flow_started'
          defaultValue={addTags.join(', ')}
          disabled={readOnly}
          onChange={event => onConfigChange({ addTags: parseTags(event.target.value) })}
        />
        {addTags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {addTags.map(tag => (
              <Chip key={tag} size='small' label={tag} />
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextField
          key={`${block.blockId}-removeTags`}
          size='small'
          label='Tags entfernen (kommagetrennt)'
          placeholder='birthday_flow_started, birthday_30_ready'
          defaultValue={removeTags.join(', ')}
          disabled={readOnly}
          onChange={event => onConfigChange({ removeTags: parseTags(event.target.value) })}
        />
        {removeTags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {removeTags.map(tag => (
              <Chip key={tag} size='small' variant='outlined' label={tag} />
            ))}
          </Box>
        )}
      </Box>
      {knownTags.length > 0 && (
        <Typography variant='caption' color='text.secondary'>
          Vorhandene Tags: {knownTags.slice(0, 12).join(', ')}
          {knownTags.length > 12 ? ' …' : ''}
        </Typography>
      )}
      <Typography variant='caption' color='text.secondary'>
        Diese Tags steuern die GetResponse-Automation (z. B. startet „birthday_flow_started“ den
        dortigen Mail-Versand). Fehlende Tags werden beim Setzen automatisch angelegt; beim
        Entfernen bleiben fremde Tags unangetastet. Für den Trigger-Tag gilt: eigener Block NACH
        dem GetResponse-Sync, nie davor.
      </Typography>
    </Box>
  );
};

export default GetResponseTagSettings;
